import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  Alert,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useGoals } from '../context/GoalsContext';
import { useIncome } from '../context/IncomeContext';
import { useSubscription } from '../context/SubscriptionContext';
import { AnalyticsService } from '../services/analyticsService';
import { createThemedStyles } from '../theme/useThemedStyles';
import { useTheme } from '../context/ThemeContext';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/date';
import { useNavigation } from '@react-navigation/native';
import AdBanner from '../components/AdBanner';
import WebPage from '../components/web/WebPage';
import PageScroll from '../components/web/PageScroll';

const { width } = Dimensions.get('window');

const AnalyticsScreen: React.FC = () => {
  const { goals } = useGoals();
  const { income } = useIncome();
  const { canShowDashboardCard, currentTier } = useSubscription();
  const navigation = useNavigation();
  const { colors } = useTheme();
  const styles = useAnalyticsStyles();

  const analyticsWebHeader = {
    title: 'Analytics Dashboard',
    subtitle: 'Comprehensive insights into your financial goals',
    onBack: () => navigation.goBack(),
  };


  // Block access for non-premium users
  if (currentTier !== 'premium') {
    return (
      <WebPage {...analyticsWebHeader}>
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 }}>
        <Ionicons name="lock-closed-outline" size={64} color={colors.primary.main} />
        <Text style={{ fontSize: 24, fontWeight: 'bold', marginTop: 16, marginBottom: 8 }}>Premium Feature</Text>
        <Text style={{ fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
          The Analytics dashboard is available exclusively for Premium plan users. Upgrade to Premium to unlock advanced analytics, insights, and recommendations.
        </Text>
        <TouchableOpacity
          style={{ backgroundColor: colors.button.primary, paddingVertical: 12, paddingHorizontal: 32, borderRadius: 8 }}
          onPress={() => navigation.navigate('Subscription')}
        >
          <Text style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}>Upgrade to Premium</Text>
        </TouchableOpacity>
        <Text style={{ marginTop: 24, color: colors.grey[600] }}>Current Plan: {currentTier.toUpperCase()}</Text>
      </View>
      </WebPage>
    );
  }

  const analytics = useMemo(() => {
    const goalAnalytics = AnalyticsService.calculateGoalAnalytics(goals);
    const financialInsights = AnalyticsService.calculateFinancialInsights(goals, income);
    const predictiveAnalytics = AnalyticsService.calculatePredictiveAnalytics(goals);
    
    return { goalAnalytics, financialInsights, predictiveAnalytics };
  }, [goals, income]);

  const renderProgressBar = (percentage: number, color: string = colors.primary.main) => (
    <View style={styles.progressContainer}>
      <View style={[styles.progressBar, { width: `${Math.min(percentage, 100)}%`, backgroundColor: color }]} />
    </View>
  );

  const renderMetricCard = (title: string, value: string | number, subtitle?: string, color?: string) => (
    <View style={[styles.metricCard, { borderLeftColor: color || colors.primary.main }]}>
      <Text style={styles.metricTitle}>{title}</Text>
      <Text style={[styles.metricValue, { color: color || colors.primary.main }]}>
        {typeof value === 'number' ? value.toFixed(2) : value}
      </Text>
      {subtitle && <Text style={styles.metricSubtitle}>{subtitle}</Text>}
    </View>
  );

  const renderGoalDetails = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>🎯 Goal Details</Text>
      {goals.map(goal => {
        const progress = (goal.currentAmount / goal.targetAmount) * 100;
        const probability = analytics.predictiveAnalytics.goalAchievementProbability[goal.id] || 0;
        const risk = analytics.predictiveAnalytics.riskAssessment[goal.id] || 'medium';
        
        return (
          <View key={goal.id} style={styles.goalDetailCard}>
            <View style={styles.goalDetailHeader}>
              <Text style={styles.goalDetailName}>{goal.name}</Text>
              <View style={[styles.riskBadge, { backgroundColor: 
                risk === 'low' ? colors.success.light :
                risk === 'medium' ? colors.warning.light :
                colors.error.light
              }]}>
                <Text style={styles.riskBadgeText}>{risk.toUpperCase()} RISK</Text>
              </View>
            </View>
            
            <View style={styles.goalDetailProgress}>
              {renderProgressBar(progress)}
              <Text style={styles.goalDetailProgressText}>{progress.toFixed(2)}%</Text>
            </View>
            
            <View style={styles.goalDetailStats}>
              <View style={styles.goalDetailStat}>
                <Text style={styles.goalDetailStatLabel}>Achievement Probability</Text>
                <Text style={styles.goalDetailStatValue}>{probability.toFixed(2)}%</Text>
              </View>
              <View style={styles.goalDetailStat}>
                <Text style={styles.goalDetailStatLabel}>Monthly Contribution</Text>
                <Text style={styles.goalDetailStatValue}>
                  {formatCurrency(goal.monthlyContribution, 'USD', '$')}
                </Text>
              </View>
              <View style={styles.goalDetailStat}>
                <Text style={styles.goalDetailStatLabel}>Remaining</Text>
                <Text style={styles.goalDetailStatValue}>
                  {formatCurrency(goal.targetAmount - goal.currentAmount, 'USD', '$')}
                </Text>
              </View>
            </View>
          </View>
        );
      })}
    </View>
  );

  const renderFinancialAnalysis = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>💰 Financial Analysis</Text>
      
      <View style={styles.metricsGrid}>
        {renderMetricCard(
          'Income Allocation',
          `${analytics.financialInsights.incomeAllocationEfficiency}%`,
          'Of income allocated to goals',
          colors.primary.main
        )}
        {renderMetricCard(
          'Savings Rate',
          `${analytics.financialInsights.savingsRateOptimization}%`,
          'Of income saved',
          colors.success.main
        )}
        {renderMetricCard(
          'Debt Payoff Speed',
          `${analytics.financialInsights.debtPayoffAcceleration.toFixed(2)}x`,
          'Faster than minimum payments',
          colors.secondary.main
        )}
      </View>

      <View style={styles.insightCard}>
        <Text style={styles.insightTitle}>💡 Income Allocation Insight</Text>
        <Text style={styles.insightText}>
          You're allocating {analytics.financialInsights.incomeAllocationEfficiency.toFixed(2)}% of your income to goals. 
          {analytics.financialInsights.incomeAllocationEfficiency > 50 
            ? ' This is excellent! You\'re prioritizing your financial goals.' 
            : ' Consider increasing your goal contributions for better progress.'}
        </Text>
      </View>
    </View>
  );

  const renderPerformanceAnalysis = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>📊 Performance Analysis</Text>
      
      <View style={styles.metricsGrid}>
        {renderMetricCard(
          'Completion Rate',
          `${analytics.goalAnalytics.completionRate.toFixed(2)}%`,
          'Goals completed successfully',
          colors.success.main
        )}
        {renderMetricCard(
          'Avg Completion Time',
          `${Math.round(analytics.goalAnalytics.averageCompletionTime)} days`,
          'Time to complete goals',
          colors.secondary.main
        )}
      </View>

      <View style={styles.performanceContainer}>
        <Text style={styles.performanceTitle}>Success Rate by Priority</Text>
        {Object.entries(analytics.goalAnalytics.successRateByPriority).map(([priority, rate]) => (
          <View key={priority} style={styles.priorityItem}>
            <Text style={styles.priorityLabel}>{priority}</Text>
            <View style={styles.priorityProgress}>
              {renderProgressBar(rate, 
                priority === 'Essential' ? colors.error.main :
                priority === 'Important' ? colors.warning.main :
                colors.success.main
              )}
              <Text style={styles.priorityRate}>{rate.toFixed(2)}%</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );

  const renderRecommendations = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>💡 Smart Recommendations</Text>
      {analytics.predictiveAnalytics.recommendations.map((recommendation, index) => (
        <View key={index} style={styles.recommendationCard}>
          <Ionicons name="bulb-outline" size={20} color={colors.warning.main} />
          <Text style={styles.recommendationText}>{recommendation}</Text>
        </View>
      ))}
    </View>
  );

  return (
    <WebPage {...analyticsWebHeader}>
    <PageScroll style={styles.container} showsVerticalScrollIndicator={false}>
      {renderPerformanceAnalysis()}
      {renderFinancialAnalysis()}
      {renderGoalDetails()}
      {renderRecommendations()}

      <View style={styles.footer}>
        <Text style={styles.footerText}>
          Analytics updated in real-time based on your goal progress
        </Text>
      </View>
      <AdBanner />
    </PageScroll>
    </WebPage>
  );
};

const useAnalyticsStyles = createThemedStyles((colors) => ({
  container: {
    flex: 1,
    backgroundColor: colors.background.default,
  },
  header: {
    backgroundColor: colors.primary.header,
    padding: 20,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    textAlign: 'center',
  },
  section: {
    marginBottom: 24,
    paddingHorizontal: Platform.OS === 'web' ? 0 : 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text.primary,
    marginBottom: 16,
  },
  metricsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  metricCard: {
    backgroundColor: colors.background.paper,
    padding: 16,
    borderRadius: 12,
    borderLeftWidth: 4,
    flex: 1,
    marginHorizontal: 4,
    marginBottom: 8,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  metricTitle: {
    fontSize: 12,
    color: colors.text.secondary,
    marginBottom: 4,
  },
  metricValue: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  metricSubtitle: {
    fontSize: 10,
    color: colors.text.secondary,
  },
  insightCard: {
    backgroundColor: colors.background.paper,
    borderRadius: 12,
    padding: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  insightTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: 8,
  },
  insightText: {
    fontSize: 14,
    color: colors.text.secondary,
    lineHeight: 20,
  },
  performanceContainer: {
    backgroundColor: colors.background.paper,
    borderRadius: 12,
    padding: 16,
  },
  performanceTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: 16,
  },
  priorityItem: {
    marginBottom: 16,
  },
  priorityLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: 8,
  },
  priorityProgress: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  priorityRate: {
    fontSize: 12,
    color: colors.text.secondary,
    marginLeft: 8,
    minWidth: 40,
  },
  progressContainer: {
    height: 8,
    backgroundColor: colors.grey[200],
    borderRadius: 4,
    flex: 1,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: 4,
  },
  goalDetailCard: {
    backgroundColor: colors.background.paper,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  goalDetailHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  goalDetailName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    flex: 1,
  },
  riskBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  riskBadgeText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.text.primary,
  },
  goalDetailProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  goalDetailProgressText: {
    fontSize: 12,
    color: colors.text.secondary,
    marginLeft: 8,
    minWidth: 40,
  },
  goalDetailStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  goalDetailStat: {
    alignItems: 'center',
    flex: 1,
  },
  goalDetailStatLabel: {
    fontSize: 10,
    color: colors.text.secondary,
    marginBottom: 4,
    textAlign: 'center',
  },
  goalDetailStatValue: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text.primary,
  },
  recommendationCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: colors.background.paper,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  recommendationText: {
    fontSize: 14,
    color: colors.text.primary,
    marginLeft: 12,
    flex: 1,
    lineHeight: 20,
  },
  footer: {
    padding: 20,
    alignItems: 'center',
  },
  footerText: {
    fontSize: 12,
    color: colors.text.secondary,
    textAlign: 'center',
  },
  upgradeContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
    backgroundColor: colors.background.default,
  },
  upgradeTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text.primary,
    marginTop: 16,
    marginBottom: 12,
  },
  upgradeText: {
    fontSize: 16,
    color: colors.text.secondary,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 24,
  },
  currentPlan: {
    fontSize: 14,
    color: colors.primary.main,
    fontWeight: '600',
    marginBottom: 24,
  },
  upgradeButton: {
    backgroundColor: colors.button.primary,
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 12,
  },
  upgradeButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
}));

export default AnalyticsScreen; 